'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import styles from './ErrorPage.module.css';

interface ErrorPageProps {
    code: number;
    title: string;
    message: string;
}

// 20 смешных надписей для 404
const jokes404 = [
    "Эта страница ушла за молоком и не вернулась",
    "404: Страница в отпуске. Будет через 2 недели. Может быть.",
    "Поздравляем! Вы нашли секретную пустоту интернета 🎉",
    "Эта страница была съедена котом. Простите.",
    "ERROR: Страница не найдена. Как и мой смысл жизни.",
    "Страница самоизолировалась. Соблюдайте дистанцию.",
    "Тут был контент, но его украли пираты 🏴‍☠️",
    "Страница ушла на пенсию. Передайте ей привет!",
    "rm -rf /page успешно выполнен. Упс.",
    "Страница существует только в параллельной вселенной",
    "git checkout --страница_не_существует",
    "Это не баг, это фича. Честно-честно.",
    "sudo apt-get install страница — пакет не найден",
    "Ваш запрос важен для нас. Но страница — нет.",
    "Страница была, но мы её потеряли. Как носок в стирке.",
    "HTTP 404: Страница улетела в Нарнию 🦁",
    "Данные удалены. Виноват стажёр. Он уже уволен.",
    "Страница сбежала с 500-й ошибкой. Мы за них рады.",
    "NULL — вот что тут находится. И это не ошибка.",
    "Вы достигли края интернета. Поздравляем! 🌍",
];

// 20 смешных надписей для 403
const jokes403 = [
    "Доступ запрещён. Как и вход в клуб без приглашения.",
    "403: У вас нет прав. Зато есть обязанности!",
    "Сюда нельзя. Это как кухня в 3 часа ночи для кота.",
    "Forbidden. Прямо как холодильник после полуночи.",
    "Ваш IP-адрес не в VIP-списке. Попробуйте подкуп.",
    "Здесь живёт дракон. Он не любит гостей 🐉",
    "Access Denied: Пароль 'admin123' не подошёл",
    "Эта дверь закрыта. Как и моё сердце после дедлайнов.",
    "403: Только для своих. А вы — чужой.",
    "Тук-тук. — Кто там? — Нет.",
    "Сюда только по пригласительным. Или с пиццей 🍕",
    "Forbidden: Требуется уровень допуска 'Начальник'",
    "Вы пытаетесь войти не в ту дверь. Она не та.",
    "403: Ваш уровень доступа — 'Посмотреть меню'",
    "Это приватная зона. Как мой личный дневник.",
    "Access Denied. Но мы всё равно вас любим ❤️",
    "Сюда нельзя. Правило №1: сюда нельзя.",
    "403: Вы не прошли тест на человечность. Или прошли?",
    "Forbidden: Сначала скажите пароль (мы его забыли)",
    "Доступ запрещён. Ничего личного, просто бизнес 💼",
];

// Общие шутки для других ошибок
const jokesGeneric = [
    "Что-то пошло не так. Мы тоже не знаем что.",
    "Ошибка произошла. Винить некого. Кроме стажёра.",
    "Сервер устал. Дайте ему отдохнуть.",
    "Кажется, мы что-то сломали. Упс.",
    "Это временно. Как и всё в этой жизни.",
];

function getJokes(code: number): string[] {
    switch (code) {
        case 403:
            return jokes403;
        case 404:
            return jokes404;
        default:
            return jokesGeneric;
    }
}

export default function ErrorPage({ code, title, message }: ErrorPageProps) {
    const [joke, setJoke] = useState('');

    useEffect(() => {
        const jokes = getJokes(code);
        setJoke(jokes[Math.floor(Math.random() * jokes.length)]);
    }, [code]);

    return (
        <div className={styles.container}>
            <div className={styles.wrapper}>
                <div className={styles.card}>
                    <div className={styles.header}>
                        <span className={styles.headerTitle}>
                            <span className={styles.blink}>● </span>
                            SYSTEM_FAILURE
                            <span className={styles.yellowDot}> ●</span>
                        </span>
                        <span className={styles.headerCode}>exit_code: {code}</span>
                    </div>

                    <div className={styles.body}>
                        <div className={styles.ascii}>
                            {`╔═══════════════════════════════╗
║  ⚠️  CRITICAL ERROR DETECTED  ║
╚═══════════════════════════════╝`}
                        </div>

                        <div className={styles.code}>
                            <span className={styles.caret}>&gt;</span>
                            {code}
                        </div>

                        <h1 className={styles.title}>{title}</h1>
                        <p className={styles.message}>{message}</p>

                        {joke && (
                            <div className={styles.joke}>
                                <div className={styles.jokeLabel}>💀 random_wisdom</div>
                                <p className={styles.jokeText}>{joke}</p>
                            </div>
                        )}

                        <div className={styles.terminal}>
                            <span className={styles.terminalPrompt}>$ </span>
                            cat /var/log/excuses.log | grep &quot;{code}&quot;
                            <span className={styles.cursor} />
                        </div>
                    </div>

                    <div className={styles.footer}>
                        <Link href="/" className={styles.linkPrimary}>[ ← HOME ]</Link>
                        <button
                            onClick={() => window.history.back()}
                            className={styles.link}
                        >
                            [ ← BACK ]
                        </button>
                        <button
                            onClick={() => window.location.reload()}
                            className={styles.link}
                        >
                            [ ↻ RETRY ]
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
